import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import {
  defaultCellRenderer,
  defaultGroupRenderer as groupRenderer, showToast
} from "../../../other/utils/utils.jsx";
import SwivuModal from "./SwivuModal.jsx";
import { useCallback } from "react";
import { Store } from "@bryntum/schedulerpro";
import { User } from "../../../bryntum-lib/models/User.jsx";

const DELIVERED_TEXT = {
  isEmailNotify: 'Email',
  isWorkforceNotify: 'Workforce',
  bannerNotify: 'Banner'
}

class SwivuBoardAlertsEditor extends EditorBase {
  static get $name() {
    return 'SwivuBoardAlertsEditor';
  }

  static get type() {
    return WIDGET_KEYS.swivuBoardAlertEditor;
  }

  static get configurable() {
    return {
      title: "Board Alert",
      minWidth: "40em",
      width: "60em",
      minHeight: "35em",
      height: "35em",
      cls: "resize minimal-gradient-editor",
      closable: true,
      autoClose: false,
      draggable: true,
      autoUpdateRecord: true,
      stateId: PERSISTENCE_STATE_KEYS.boardAlertsEditor,
      bodyCls: "!gap-0",
      defaultFocus: (w) => w.ref === "titleInput",
      listeners: {
        beforeHide: "onEditorHide"
      },
      tbar: {
        cls: "!border-0 bg-[#fff] dark:bg-[#211F21] p-4 !pb-0",
        contentElementCls: "!p-0 !min-h-fit",
        layoutStyle: {
          justifyContent: "space-between",
          flexDirection: "column",
          flexWrap: "nowrap"
        },
        items: {
          titleInput: {
            type: "textfield",
            field: "title",
            name: "title",
            cls: "w-full boardalert-items-title-input !mb-[0.5em]",
            placeholder: "Board Alert Title",
            onInput: "up.onChangeFields",
          },
          descriptionField: {
            type: "textarea",
            height: 95,
            width: "100%",
            name: "description",
            onInput: "up.onChangeFields",
            placeholder: "Board Alert Description",
            cls: "w-[100%] !mb-[0.5em]"
          },
          searchField: {
            type: "text",
            placeholder: "Find a Board User",
            onInput: "up.onSearchChange",
            onClear: "up.onSearchClear",
            clearable: true,
            flex: 1,
            cls: "swivu-finder-search !w-full !mb-[0.5em]",
            triggers: {
              searchIcon: {
                cls: 'b-fa b-fa-search pt-[1px]',
                align: "start",
              },
            }
          }
        }
      },
      items: {
        boardAlertGrid: {
          cls: "w-full",
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          height: '100%',
          bodyCls: "!mt-[14px]",
          stateId: PERSISTENCE_STATE_KEYS.boardAlertsEditor,
          features: {
            cellMenu: false,
            sort: {multiSort: false}
          },
          selectionMode: { checkbox: true, checkboxOnly: true, showCheckAll: true },
          columns: [
            {
              text: "Board User",
              field: "name",
              editor: false,
              sortable: true,
              groupable: true,
              flex: 1,
            },
            {
              id: 'user-role',
              text: "Role",
              field: "role",
              displayField: "name",
              editor: false,
              sortable: true,
              groupable: true,
              flex: 1,
              groupRenderer({groupRecords, groupRowFor, count}) {
                return groupRowFor ? groupRenderer({ groupRowFor: groupRecords[0].roleName, count }): defaultCellRenderer({ value: null });
              },
              renderer: ({ record }) => {
                return record.roleName
              }
            },
            {
              id: 'delivered',
              text: "Delivered",
              field: "delivered",
              sortable: true,
              groupable: false,
              editor: false,
              flex: 1,
            }
          ],
          listeners: {
            selectionChange: "up.onGridSelectionChange"
          },
        }
      },
      bbar: {
        cls: "dark:!bg-[#2c2a2e]",
        items: {
          sendButton: {
            onClick: "up.onSendBoardAlert",
            cls: 'bg-[transparent] text-[#228a0c] border-[1.5px] !rounded-md',
            text: "Send Alert",
            icon: "fa-light fa-paper-plane-top",
            disabled: true,
          },
          closeButton: {
            type: "button",
            icon: "fa-light fa-close",
            text: "Close",
            cls: 'bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md',
            onClick: "up.close",
          },
        }
      }
    }
  }

  updateRecipientsCell(recipientRecords, reminderId) {
    recipientRecords.forEach((recipientRecord) => {
      if (recipientRecord.reminderId === reminderId) {
        const channelsWhereAlertsSent = Object.keys(DELIVERED_TEXT).reduce((accum, value) => {
          if (recipientRecord[value]) {
            accum.push(DELIVERED_TEXT[value]);
          }
          return accum;
        }, []).join(' | ');
        recipientRecord.boardUser.delivered = channelsWhereAlertsSent;
      }
    })
  }

  editRecord({  reminderRecord, defaultSelectedBoardUsers = [] }, context, otherParams) {
    super.editRecord(reminderRecord, context, otherParams);

    const { boardAlertGrid} = this.widgetMap;

    if (otherParams.isFirstShow) {
      this.record = reminderRecord.copy();
      // opening existing board alert record empty the boardUserSeen
      if (!reminderRecord.isPhantom) {
        this.record.boardUserSeen = [];
      }
      Object.assign(this, context, otherParams);
      this.context = context;
      // adding layout direction on tbar using widget config above doesn't work
      this.widgetMap.tbar.layout.direction = "column";
      this.changeCrossToEsc();

      boardAlertGrid.store = new Store({
        data: this.project.boardUsersStore.query((rec) => {
          // this will be cached, clearing it so next time when opened not displaying the old values
          rec.delivered = null;
          return true;
        }),
        modelClass: User
      })
      boardAlertGrid.store.sort('name', true);

      if (defaultSelectedBoardUsers.length) {
        boardAlertGrid.selectedRecords = defaultSelectedBoardUsers;
      } else if (!reminderRecord.isPhantom) { // for existing records display status column values
        boardAlertGrid.selectedRecords = reminderRecord.recipients.map((recipient) => recipient.boardUser.id);
        boardAlertGrid.selectedRecords.forEach((userRecord) => {
          this.updateRecipientsCell(userRecord.alertRecipients, reminderRecord.id);
        })
      }

      // update status on receiving from websocket
      this.remRecStoreDestroyListener = this.context.project.reminderRecipientStore.on({
        update({ records: recipientRecords }) {
          this.updateRecipientsCell(recipientRecords, this.record.id);
        },
        thisObj: this,
      })
    }
  }

  toggleSendButtonDisable() {
    const { titleInput, descriptionField, boardAlertGrid, sendButton } = this.widgetMap;
    const isPopupHasData = boardAlertGrid.selectedRecords.length && titleInput.value?.trim() && descriptionField.value?.trim();
    sendButton.disabled = !isPopupHasData || !this.record.isPhantom;
  }

  onChangeFields() {
    this.toggleSendButtonDisable();
  }

  onGridSelectionChange() {
    this.toggleSendButtonDisable();
  }

  onSearchChange({ value }) {
    const { boardAlertGrid } = this.widgetMap;
    boardAlertGrid.store.filter({
      property: 'name',
      value,
      caseSensitive: false,
      exactMatch: false
    });
  }

  onSearchClear() {
    this.onSearchChange({ value: '' });
  }

  onSendBoardAlert() {
    const { boardAlertGrid, titleInput, descriptionField, sendButton } = this.widgetMap;
    const selectedRecords = boardAlertGrid.selectedRecords;

    if (selectedRecords.length && titleInput.value && descriptionField.value) {
     // FIX: Create records first, then assign properties to avoid ID assignment issues
    //
    // BUG CONTEXT: When reminderRecipientStore is empty and records are added via server sync/websocket,
    // the createRecord() method with constructor arguments can cause ID assignment issues where
    // reminderId is not properly assigned to the new record.
    //
    // SOLUTION: Create the record first without arguments, then explicitly assign the properties
    // after the record is fully initialized. This ensures proper property assignment regardless
    // of the store's state or server sync timing.
    const reminderRecipients = selectedRecords.map((boardUser) => {
      const newRecord = this.project.reminderRecipientStore.createRecord();
      newRecord.reminderId = this.record.id;
      newRecord.boardUserId = boardUser.id;
      return newRecord;
    });

      this.project.reminderStore.add(this.record);
      this.project.reminderRecipientStore.add(reminderRecipients);

      // meanwhile until the response is resolved we need to display some temporary value
      selectedRecords.forEach((record) => {
        record.delivered = "Retrieving Sent Status...";
      });

      showToast({
        type: "success",
        msg: "Board Alert sent",
      });

      sendButton.disabled = true;
      this.eachWidget(w => w.readOnly = true);
    }
  }

  onEditorHide() {
    if (this.remRecStoreDestroyListener) {
      this.remRecStoreDestroyListener();
      this.remRecStoreDestroyListener = null;
    }
  }
}

SwivuBoardAlertsEditor.initClass();

const SwivuBoardAlertsPopup = ({ context, record, onClose, isShow, editorInstance }) => {

  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord(record, context, Object.assign(otherParams));
    editorInstance.editorRef = editor;
  }, [context, editorInstance, record]);

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuBoardAlertEditor}
      setFontSize={context.setFontSize}
      rowHeight={context.rowHeight}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  )
}

export default SwivuBoardAlertsPopup
